import { CommonModule, DatePipe, NgForOf, NgIf } from "@angular/common";
import { Component, inject, OnInit } from "@angular/core";
import { getHttpErrorMessage } from "../../../shared/utils/http-error.util";
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { ActivatedRoute, Router, RouterLink } from "@angular/router";
import {
  TuiAlertService,
  TuiAppearance,
  TuiButton,
  TuiDataList,
  TuiDialogService,
  TuiDropdown,
  TuiError,
  TuiLoader,
  TuiSizeL,
  TuiSizeS,
  TuiTextfield,
} from "@taiga-ui/core";
import {
  TuiBadgedContent,
  TuiDataListWrapper,
  TuiItemsWithMore,
  TuiPagination,
  TuiTabs,
  tuiValidationErrorsProvider,
  TuiFieldErrorPipe,
} from "@taiga-ui/kit";
import { TuiNavigation, TuiSearch } from "@taiga-ui/layout";
import { TuiTable } from "@taiga-ui/addon-table";
import {
  TuiComboBoxModule,
  TuiInputDateRangeModule,
  TuiInputModule,
  TuiInputPasswordModule,
} from "@taiga-ui/legacy";
import { ClientService } from "../../../core/services/client.service";
import { ForgotResetPasswordService } from "../../../core/services/forgot-reset-password.service.service";
import { InvoiceService } from "../../../core/services/invoice.service";
import { catchError } from "rxjs";
import { IShift } from "../../../model/interface/shift";
import { formatClientIdentifier } from "../../../shared/utils/client-identifier";
import { TuiDayRange } from "@taiga-ui/cdk";
import { ShiftService } from "../../../core/services/shift.service";
import { ChargesService } from "../../../core/services/charges.service";
import { EnterSubmitDirective } from "../../../core/directives/enter-submit.directive";

@Component({
  selector: "app-client-profile",
  imports: [
    EnterSubmitDirective,
    FormsModule,
    NgForOf,
    NgIf,
    TuiAppearance,
    TuiDataListWrapper,
    TuiDataList,
    TuiDropdown,
    TuiTable,
    TuiNavigation,
    TuiTabs,
    TuiTextfield,
    TuiItemsWithMore,
    TuiSearch,
    ReactiveFormsModule,
    CommonModule,
    TuiBadgedContent,
    TuiInputModule,
    TuiButton,
    TuiComboBoxModule,
    TuiPagination,
    TuiFieldErrorPipe,
    TuiError,
    TuiInputModule,
    TuiInputPasswordModule,
    TuiInputDateRangeModule
  ],
  templateUrl: "./client-profile.component.html",
  styleUrl: "./client-profile.component.scss",
  providers: [
    DatePipe,
    tuiValidationErrorsProvider({
      required: "This field is required",
      minLength: "Password must be at least 8 characters",
      pattern:
        "Password must contain at least 8 characters, one uppercase letter, one lowercase letter, one number, and one special character",
      passwordMismatch: "Passwords do not match",
    }),
  ],
})
export class ClientProfileComponent {
  clientId: string = "";
  client: any | null = null;
  isLoading: boolean = false;

  invoiceData: any[] = [];
  filters = {
    searchText: "",
    status: null,
  };
  pageIndex = 1;
  pageSize = 5;
  total: number = 0;
  sortField = "createdAt";
  sortOrder: number = -1;
  size: TuiSizeL | TuiSizeS = "s";
  changePasswordForm: any;

  shifts: IShift[] = [];
  shiftPageIndex = 1;
  shiftPageSize = 5;
  shiftTotal: number = 0;
  filterStartDate: string = "";
  filterEndDate: string = "";
  dateRange: TuiDayRange | null = null;

  chargesPageIndex = 1;
  chargesPageSize = 5;
  chargesTotal: number = 0;
  chargesRecords: any[] = [];

  protected readonly statusItems = [
    { status: "Draft", value: "Draft" },
    { status: "Sent", value: "Sent" },
  ];

  constructor(
    private clientService: ClientService,
    private invoiceService: InvoiceService,
    private router: Router,
    private route: ActivatedRoute,
    private resetPasswordService: ForgotResetPasswordService,
    private alerts: TuiAlertService,
    private dialogs: TuiDialogService,
    private fb: FormBuilder,
    private shiftService: ShiftService,
    private chargesService: ChargesService
  ) {
    this.changePasswordForm = this.fb.group(
      {
        oldPassword: [null, [Validators.required]],
        password: [
          null,
          [
            Validators.required,
            Validators.pattern(
              /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/
            ),
          ],
        ],
        confirmPassword: [null, [Validators.required]],
      },
      {
        validator: this.passwordMatchValidator,
      }
    );
  }

  ngOnInit() {
    this.clientId = this.route.snapshot.paramMap.get("id")!;
    if (this.clientId) {
      this.loadClientData();
      this.loadInvoices();
    }
    this.loadShifts();
    this.loadChargesRecords();
  }

  loadClientData(): void {
    this.isLoading = true;
    this.clientService.getOneClientById(this.clientId).subscribe({
      next: (res: any) => {
        this.client = res;
      },
      error: (error) => {
        console.error("Error fetching client data:", error);
      },
      complete: () => {
        this.isLoading = false;
      },
    });
  }

  loadShifts(): void {
    const payload = {
      pageIndex: this.shiftPageIndex,
      pageSize: this.shiftPageSize,
      sortField: "date",
      sortOrder: -1,
      startDate: this.filterStartDate, // ISO string, e.g., "2025-02-01T00:00:00Z"
      endDate: this.filterEndDate, // ISO string, e.g., "2025-02-20T23:59:59Z"
    };

    this.shiftService
      .getPagedClientShift(payload, this.clientId)
      .subscribe((res: any) => {
        if (res && res.data && res.data.length) {
          const pagedResult = res.data[0];
          this.shifts = pagedResult.data;
          this.shiftTotal =
            (pagedResult.metadata && pagedResult.metadata[0]?.total) || 0;
        } else {
          this.shifts = [];
          this.shiftTotal = 0;
        }
      });
  }

  onFilter(): void {
    if (this.dateRange) {
      const startDate = this.dateRange.from.toLocalNativeDate();
      const endDate = this.dateRange.to.toLocalNativeDate();

      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(23, 59, 59, 999);

      this.filterStartDate = startDate.toISOString();
      this.filterEndDate = endDate.toISOString();
    } else {
      this.filterStartDate = "";
      this.filterEndDate = "";
    }

    this.shiftPageIndex = 1;
    this.loadShifts();
  }

  resetFilter(): void {
    this.dateRange = null;
    this.filterStartDate = "";
    this.filterEndDate = "";
    this.shiftPageIndex = 1;
    this.loadShifts();
  }

  loadChargesRecords(): void {
    const payload = {
      pageIndex: this.chargesPageIndex,
      pageSize: this.chargesPageSize,
      sortField: "year",
      sortOrder: -1,        
    };
  
    this.shiftService.getMonthlyShiftsByClientIdPaginated(this.clientId,payload).subscribe(
      (res: any) => {
        console.log("Paged charges records:", res.data);
        if (res.data && res.data[0]) {
          const pagedResult = res.data[0];
          this.chargesRecords = pagedResult.data;
          this.chargesTotal = (pagedResult.metadata && pagedResult.metadata[0]?.total) || 0;
        } else {
          this.chargesRecords = [];
          this.chargesTotal = 0;
        }
      },
      (error) => {
        console.error(error);
      }
    );
  }

  loadInvoices(): void {
    const payload = {
      pageIndex: this.pageIndex,
      pageSize: this.pageSize,
      filters: {
        clientId: this.clientId,
        ...this.filters,
        // Force the status filter to "Sent"
        status: "Sent",
      },
      sortField: this.sortField,
      sortOrder: this.sortOrder,
    };

    this.isLoading = true;
    this.invoiceService.getPagedInvoices(payload).subscribe({
      next: (res: any) => {
        if (res && res.length > 0 && res[0].data) {
          // Filter invoices by client and ensure the status is "Sent"
          this.invoiceData = res[0].data.filter(
            (invoice: any) =>
              invoice?.client?._id === this.clientId && invoice.status === "Sent"
          );
          this.total = this.invoiceData.length;
        } else {
          this.invoiceData = [];
          this.total = 0;
        }
      },
      error: (error) => {
        console.error("Error fetching invoices:", error);
      },
      complete: () => {
        this.isLoading = false;
      },
    });
  }

  applyFilters(newFilters: any): void {
    this.filters = { ...this.filters, ...newFilters };
    // Reset to first page when filters change
    this.pageIndex = 1;
    this.loadInvoices();
  }

  onEnter(event: KeyboardEvent): void {
    if (event.key === "Enter") {
      this.applyFilters({ searchText: this.filters.searchText });
    }
  }

  clearSearch(): void {
    this.filters.searchText = "";
    this.filters.status = null;
    this.applyFilters({ searchText: "", status: null });
  }

  onPageChange(page: number): void {
    this.pageIndex = page;
    this.loadInvoices();
  }

  getPageCount(): number {
    return Math.ceil(this.total / this.pageSize);
  }

  passwordMatchValidator(
    formGroup: FormGroup
  ): { [key: string]: boolean } | null {
    if (
      formGroup.get("password")?.value !==
      formGroup.get("confirmPassword")?.value
    ) {
      return { passwordMismatch: true };
    }
    return null;
  }

  submitChangePassword(): void {
    if (this.changePasswordForm.valid && this.clientId) {
      this.isLoading = true;
      const payload = {
        id: this.clientId,
        oldPassword: this.changePasswordForm.value.oldPassword,
        password: this.changePasswordForm.value.password,
      };

      this.clientService
        .changePassword(payload)
        .pipe(
          catchError((error) => {
            this.isLoading = false;
            this.changePasswordForm.setErrors({ invalid: true });
            this.alerts
              .open(getHttpErrorMessage(error), {
                label: "Error",
                appearance: "negative",
              })
              .subscribe();
            this.changePasswordForm.reset();
            return [];
          })
        )
        .subscribe((response: any) => {
          this.isLoading = false;
          if (response?.oldPwdCheck) {
            this.alerts
              .open(response.message, {
                label: "Error",
                appearance: "negative",
              })
              .subscribe();
          } else {
            this.alerts
              .open("Password changed successfully.", {
                label: "Success",
                appearance: "success",
              })
              .subscribe();
            this.changePasswordForm.reset();
          }
        });
    } else if (this.changePasswordForm.hasError("passwordMismatch")) {
      this.alerts
        .open("Passwords do not match.", {
          label: "Error",
          appearance: "negative",
        })
        .subscribe();
    } else {
      this.changePasswordForm.markAllAsTouched();
      this.alerts
        .open("Form is invalid.", {
          label: "Error",
          appearance: "negative",
        })
        .subscribe();
    }
  }

  getShiftPageCount(): number {
    return Math.ceil(this.shiftTotal / this.shiftPageSize);
  }

  onShiftPageChange(page: number): void {
    this.shiftPageIndex = page;
    this.loadShifts()
  }

  getFormattedDate(date: Date | string): string {
    const dateObj = date instanceof Date ? date : new Date(date);
    
    if (isNaN(dateObj.getTime())) {
      return 'Invalid Date';
    }
    
    const day = dateObj.getDate();
    const monthNames = [
      "January", "February", "March", "April", "May", "June", "July",
      "August", "September", "October", "November", "December"
    ];
    const month = monthNames[dateObj.getMonth()];
    const year = dateObj.getFullYear();
  
    // Function to get ordinal suffix
    const getOrdinalSuffix = (day: number): string => {
      if (day > 3 && day < 21) return "th"; 
      switch (day % 10) {
        case 1: return "st";
        case 2: return "nd";
        case 3: return "rd";
        default: return "th";
      }
    };
  
    return `${day}${getOrdinalSuffix(day)} ${month} ${year}`;
  }

  getMonth(date: Date): number {
    return new Date(date).getMonth() + 1; // getMonth() is zero-indexed, so add 1
  }

  getYear(date: Date): number {
    return new Date(date).getFullYear();
  }

  getChargesPageCount(): number {
    return Math.ceil(this.chargesTotal / this.chargesPageSize);
  }

  onChargesPageChange(page: number): void {
    this.chargesPageIndex = page;
    this.loadChargesRecords();
  }

  viewChargesDetails(clientId: any, month: any, year: any): void {
    this.router.navigate([`/invoice/${clientId}/${month}/${year}`]);
  }

  protected activeItemIndex = 0;

  protected onClick(item: string): void {
    this.alerts.open(item).subscribe();
  }

  /** Resolves the client type with a Registered fallback for legacy rows. */
  get clientType(): string {
    return this.client?.clientType || "Registered";
  }

  get isRegistered(): boolean {
    return this.clientType === "Registered";
  }

  get isUnregistered(): boolean {
    return this.clientType === "Unregistered";
  }

  get isInward(): boolean {
    return this.clientType === "Inward";
  }

  /** Identifier shown in the page subheader (REG- / NREG- / BHT-). */
  get clientIdentifier(): string {
    return formatClientIdentifier(this.client);
  }

  /** Human-readable age combining years and months, e.g. "3 Years 6 Months". */
  formatAge(client: any): string {
    const parts: string[] = [];
    if (client?.age) parts.push(`${client.age} Years`);
    if (client?.ageMonths) parts.push(`${client.ageMonths} Months`);
    return parts.length ? parts.join(" ") : "-";
  }

  /** Friendly label describing how the client was on-boarded. */
  get clientOnboardingLabel(): string {
    if (!this.client) return "";
    if (this.isInward) {
      const date = this.client.inwardDate;
      return date
        ? `Admitted on ${this.formatHumanDate(date)}`
        : "Inward client";
    }
    if (this.isUnregistered) {
      const date = this.client.startingDate;
      return date
        ? `Started on ${this.formatHumanDate(date)}`
        : "Non Registered client";
    }
    const date = this.client.registrationDate;
    return date
      ? `Registered on ${this.formatHumanDate(date)}`
      : "Registered client";
  }

  private formatHumanDate(date: any): string {
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  }
}
